// ============================================================
// AssistantBar-Engine — Ausführung der Voice-Werkzeuge (function calls)
// + ui_command-Dispatch. 1:1 aus AssistantBar.js (Vanilla) verschoben.
// ============================================================
import { dispatchUiCommand, getOpenDocument, setOpenDocument, getSendSheetOpen } from '../../../core/uiCommands.ts';
// @ts-expect-error — core/voiceTargets.js ist reines JS ohne Typen
import { collectVoiceTargets } from '../../../core/voiceTargets.js';
import { toolFetch, VOICE_OFFER_URL, VOICE_SEND_URL, VOICE_RUNTOOL_URL } from './api.ts';
import { getTurnKey } from '../../../core/assistantTurn.ts';
import type { AssistantEngine } from './AssistantEngine.ts';

/* eslint-disable @typescript-eslint/no-explicit-any */

// Nach einem erfolgreich dispatchten OPEN_* wird das Panel versteckt
// (hidePanelForNavigation) — Liste der Navigations-Kommandos, geteilt
// zwischen Voice-Pfad (hier) und Text-Pfad (AssistantEngine.sendText).
export const NAV_UI_COMMANDS = new Set(['OPEN_OFFER', 'OPEN_LEAD', 'OPEN_VIEW', 'OPEN_NEW_OFFER', 'OPEN_EMAIL']);

// Befehle, die nur eine offene Oberfläche aufräumen (Karte schliessen).
// Findet sich kein Handler, ist NICHTS schiefgelaufen — sie dürfen dem
// Modell darum nie als «Bildschirm nicht geöffnet» zurückgemeldet werden.
const ADVISORY_UI_COMMANDS = new Set(['OUTBOUND_RESOLVED']);

export async function runVoiceTool(e: AssistantEngine, name: string, args: any): Promise<any> {
  e.vdbg('tool: ' + name + ' | offer=' + (e.voiceOfferId ? 'yes' : 'NO'));
  try {
    if (name === 'create_offer_draft') {
      const res = await toolFetch(VOICE_OFFER_URL, {
        method: 'POST',
        body: JSON.stringify(args || {}),
      });
      const d = await res.json();
      if (d.success) {
        e.voiceOfferId = d.offer_id;
        e.addOfferCard(d);  // namayesh-e kart-e offer dar chat
        return { result: 'Offer created', offer_number: d.offer_number, summary: d.summary, ask_read: true };
      }
      return { result: 'error', detail: d.error };
    }
    if (name === 'end_voice_session') {
      // NICHT sofort kappen — sonst bricht das «Tschüss» mitten im Wort ab.
      // Nur vormerken; voice.ts legt auf, wenn der Satz zu Ende gesprochen ist.
      e.voiceEndAfterTurn = true;
      return { result: 'Goodbye accepted. Say ONE short farewell sentence in the user\'s language now and then stop — the microphone switches itself off when you have finished speaking.' };
    }
    if (name === 'request_email') {
      if (!e.voiceOfferId) return { result: 'error', detail: 'no offer yet' };
      e.showEmailForm(e.voiceOfferId);
      return { result: 'email form opened' };
    }
    if (name === 'send_offer_whatsapp') {
      if (!e.voiceOfferId) return { result: 'error', detail: 'no offer yet' };
      const res = await toolFetch(VOICE_SEND_URL, {
        method: 'POST',
        body: JSON.stringify({ offer_id: e.voiceOfferId, phone_number: (args || {}).phone_number, country_code: (args || {}).country_code }),
      });
      if (res.status === 403) return { result: 'permission_denied', detail: 'User is not allowed to send. Tell them they do not have permission to send to customers.' };
      const d = await res.json();
      return d.success ? { result: 'Sent', sent_to: d.sent_to } : { result: 'error', detail: d.message || d.error };
    }
    if (name === 'open_customer_form') {
      // aval backend dade-ha ra normalize konad va prefill besazad
      const res = await toolFetch(VOICE_RUNTOOL_URL, {
        method: 'POST',
        body: JSON.stringify({ name: 'open_customer_form', args: args || {} }),
      });
      if (res.status === 403) return { result: 'permission_denied', detail: 'User is not allowed to create customers.' };
      const d = await res.json();
      const r = d && d.result;
      if (d.success && r && r.result === 'open_form') {
        e.showCustomerForm(r.prefill || {});
        return { result: 'form_opened', instruction: 'A pre-filled customer form is now visible on the screen. You MUST now tell the manager out loud (in their language) that a form has opened, ask them to check that the name and details are written correctly, correct anything wrong directly in the form, and then click the Erstellen button to create the customer.' };
      }
      return { result: 'error', detail: (r && r.detail) || d.error };
    }
    // tool-haye dade-i (find_customer, get_customer_history, create_customer, list_documents, ...) -> backend registry
    // ui_context: sanad-e baz rooye safhe — diktat-tool-ha be HAMIN sanad mizanand.
    const res = await toolFetch(VOICE_RUNTOOL_URL, {
      method: 'POST',
      // turn_key/send_sheet: woran der Server erkennt, ob zwischen «soll ich
      // senden?» und «ja» wirklich der Nutzer gesprochen hat. Im Voice-Pfad
      // ist jeder Werkzeugaufruf eine eigene Anfrage — der Server kann sich
      // das nicht selbst merken, die Wahrheit steht hier auf dem Client.
      body: JSON.stringify({ name, args: args || {}, ui_context: { open_document_id: getOpenDocument(), voice_targets: collectVoiceTargets(), turn_key: getTurnKey(), send_sheet: getSendSheetOpen() } }),
    });
    if (res.status === 403) return { result: 'permission_denied', detail: 'User is not allowed to do this.' };
    const d = await res.json();
    const r0 = d.success ? d.result : null;
    // ── ui_command: tool mikhahad app navigate konad (open_lead/open_view/…)
    // → dispatch rooye UI-Command-Bus; shell-e fa'al (mobile/desktop) ejra
    // mikonad. Agar hich handler-i nabud, be model sadeghane migim ke
    // safhe dar in nama baz nashod (masalan data-action anjam shode ast).
    if (r0 && r0.ui_command) {
      const handled = dispatchUiCommand(r0.ui_command);
      if (!handled && !ADVISORY_UI_COMMANDS.has(r0.ui_command.type)) {
        return { ...r0, ui_command: undefined, result: 'ui_unavailable', instruction: 'The requested screen could not be opened in the current view. Tell the user what DID happen (e.g. a draft was still created with its number) without claiming the screen opened.' };
      }
      // Das eben geöffnete Dokument SOFORT als UI-Kontext merken — nicht erst,
      // wenn das Sheet fertig montiert ist und sich selbst meldet. Ruft das
      // Modell direkt danach das nächste Tool («…und schick sie»), muss
      // open_document_id schon auf dem NEUEN Dokument stehen — sonst traf
      // der Send-Dialog nach einer Umwandlung noch die alte Offerte.
      if (r0.ui_command.type === 'OPEN_OFFER' && r0.ui_command.documentId) {
        setOpenDocument(String(r0.ui_command.documentId));
      }
      // Screen ist offen — Panel weg (Voice-Session läuft weiter), sonst
      // sieht der Nutzer weiter den Assistenten statt des Ziel-Screens.
      if (NAV_UI_COMMANDS.has(r0.ui_command.type)) e.hidePanelForNavigation();
    }
    // ── document_id: yek tool in document ra taghir dad → view-haye baz
    // (Mobile Offer-Sheet) hamin lahze live refresh mishavand.
    if (r0 && r0.document_id) {
      dispatchUiCommand({ type: 'DOCUMENT_CHANGED', documentId: String(r0.document_id) });
    }
    // ── appointments_changed: Termin per Voice angelegt/gelöscht → Kalender live
    if (r0 && r0.appointments_changed) {
      dispatchUiCommand({ type: 'APPOINTMENTS_CHANGED' });
    }
    return d.success ? d.result : { result: 'error', detail: d.error };
  } catch (err: any) {
    if (err && err.name === 'AbortError') {
      return { result: 'error', detail: 'The tool call timed out and was cancelled. Tell the user it did not complete and they can try again.' };
    }
    return { result: 'error', detail: err.message };
  }
}
